/**
 * "Agora": o motor neste instante -- quem esta na janela de 30 min, na fila
 * da regua, em ligacao, aguardando resposta e com o comercial.
 *
 * Decisoes nao obvias:
 *  1. E um RETRATO, nao um agregado do periodo: os numeros nao respeitam o
 *     filtro do cabecalho e a descricao diz isso. Misturar os dois faria o
 *     lojista somar "na fila agora" com "recuperados nos ultimos 30 dias".
 *  2. Todo "ha X min" e calculado contra `capturedAt` (relogio do servidor),
 *     nunca contra o relogio do navegador: na previa o dado e ficticio e em
 *     producao o servidor e a fonte.
 *  3. A janela de 30 min e POS-FATO: o checkout ja foi abandonado; o motor so
 *     espera para nao ligar para quem vai pagar sozinho nos proximos minutos
 *     (quem paga nesse tempo sai sem contato e sem comissao). O texto da
 *     celula diz exatamente isso.
 *  4. Ponto pulsando so quando ha ligacao em curso: movimento sem evento e
 *     decoracao, e `prefers-reduced-motion` ja desliga a animacao no CSS.
 */

import type { ReactNode } from 'react';
import { ErrorPanel } from '../../../components/PageState';
import { Badge, Footnote, Icon, LinkButton, SectionCard, type IconName } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents, formatCountCompact, formatDateTime } from '../../../lib/format';
import { descreverRelativo } from '../lib/tempo';
import type { LiveSnapshot } from '../types';

const INTERVALO_TEXTO = 'atualiza a cada 15 s';

type TomDaCelula = 'accent' | 'brand' | 'info' | 'good' | 'violet' | 'neutral';

const ICONE: Readonly<Record<TomDaCelula, string>> = {
  accent: 'bg-accent/10 text-accent',
  brand: 'bg-brand/10 text-brand',
  info: 'bg-info/10 text-info',
  good: 'bg-status-good/10 text-status-good',
  violet: 'bg-series-7/10 text-series-7',
  neutral: 'bg-ink/5 text-ink-muted',
};

export function MotorAgora({ estado, onRetry }: { readonly estado: AsyncState<LiveSnapshot>; readonly onRetry: () => void }): JSX.Element {
  const dados = estado.status === 'success' ? estado.data : null;
  return (
    <SectionCard
      id="agora"
      title="Agora"
      icon="raio"
      description="O motor neste instante. É um retrato de agora, não a soma do período do cabeçalho."
      aside={
        dados === null ? null : (
          <>
            <Badge tone="good" dot>
              Ao vivo
            </Badge>
            <span className="text-xs text-ink-subtle">
              Retrato de {formatDateTime(dados.capturedAt)} · {INTERVALO_TEXTO}
            </span>
          </>
        )
      }
    >
      {estado.status === 'loading' ? (
        <div aria-hidden="true" className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-line bg-elevated" />
          ))}
        </div>
      ) : null}
      {estado.status === 'error' ? <ErrorPanel title="Não foi possível ler o motor agora" error={estado.error} onRetry={onRetry} /> : null}
      {dados === null ? null : <Retrato dados={dados} />}
    </SectionCard>
  );
}

function Retrato({ dados }: { readonly dados: LiveSnapshot }): JSX.Element {
  const parado =
    dados.coolingCount === 0 &&
    dados.queuedCount === 0 &&
    dados.callsInProgress === 0 &&
    dados.awaitingReplyCount === 0 &&
    dados.withSellerCount === 0 &&
    dados.todayEnteredCount === 0;

  if (parado && dados.lastEventAt === null) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink">Motor ligado e à espera do primeiro evento.</p>
        <p className="text-sm text-ink-muted">
          Nada acontece antes do fato: o 1º toque sai 30 min depois de um abandono de checkout ou no dia seguinte a um vencimento de assinatura.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Celula
          icone="relogio"
          tom="accent"
          rotulo="Na janela de 30 min"
          valor={formatCountCompact(dados.coolingCount)}
          apoio={
            dados.coolingCount === 0 ? 'nenhum abandono recente' : <>abandonaram há pouco · {formatBRLFromCents(dados.coolingAmountCents)}. Quem pagar sozinho até lá sai sem contato.</>
          }
        />
        <Celula
          icone="funil"
          tom="brand"
          rotulo="Na fila da régua"
          valor={formatCountCompact(dados.queuedCount)}
          apoio={
            dados.queuedCount === 0 ? 'fila vazia' : (
              <>
                {formatBRLFromCents(dados.queuedAmountCents)} · próximo toque {descreverRelativo(dados.nextTouchAt, dados.capturedAt)}
              </>
            )
          }
        />
        <Celula
          icone="telefone"
          tom="info"
          rotulo="Ligações em curso"
          valor={formatCountCompact(dados.callsInProgress)}
          apoio={dados.callsInProgress === 0 ? 'IA sem ligação neste instante' : 'IA ao telefone agora'}
          pulsar={dados.callsInProgress > 0}
        />
        <Celula
          icone="whatsapp"
          tom="good"
          rotulo="Aguardando resposta"
          valor={formatCountCompact(dados.awaitingReplyCount)}
          apoio="WhatsApp enviado nas últimas 24 h, sem resposta nem pagamento"
        />
        <Celula
          icone="resposta"
          tom="violet"
          rotulo="Com o comercial"
          valor={formatCountCompact(dados.withSellerCount)}
          apoio={dados.withSellerCount === 0 ? 'ninguém esperando o comercial' : 'responderam; a régua parou e a venda é da vendedora'}
          acao={
            dados.withSellerCount === 0 ? null : (
              <LinkButton to="/campanhas" variant="ghost" size="sm" iconRight="seta-dir">
                Ver contatos
              </LinkButton>
            )
          }
        />
        <Celula
          icone="moeda"
          tom="neutral"
          rotulo="Hoje"
          valor={`${formatCountCompact(dados.todayRecoveredCount)} ${dados.todayRecoveredCount === 1 ? 'venda' : 'vendas'}`}
          apoio={
            <>
              {formatBRLFromCents(dados.todayRecoveredAmountCents)} recuperados · {formatCountCompact(dados.todayEnteredCount)} entraram
            </>
          }
        />
      </ul>
      <Footnote>
        Último evento processado {descreverRelativo(dados.lastEventAt, dados.capturedAt)}.
        {dados.highValueOpen.count > 0 ? (
          <>
            {' '}
            {formatCountCompact(dados.highValueOpen.count)} {dados.highValueOpen.count === 1 ? 'lead' : 'leads'} em aberto acima de{' '}
            {formatBRLFromCents(dados.highValueOpen.thresholdCents)} somam {formatBRLFromCents(dados.highValueOpen.amountCents)}.
          </>
        ) : null}
      </Footnote>
    </div>
  );
}

function Celula({
  icone,
  tom,
  rotulo,
  valor,
  apoio,
  acao = null,
  pulsar = false,
}: {
  readonly icone: IconName;
  readonly tom: TomDaCelula;
  readonly rotulo: string;
  readonly valor: string;
  readonly apoio: ReactNode;
  /** Atalho opcional, embaixo do texto de apoio. */
  readonly acao?: ReactNode;
  readonly pulsar?: boolean;
}): JSX.Element {
  return (
    <li className="flex min-w-0 flex-col gap-1.5 rounded-xl border border-line bg-elevated px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <span aria-hidden="true" className={`grid h-7 w-7 place-items-center rounded-lg ${ICONE[tom]}`}>
          <Icon name={icone} size="sm" />
        </span>
        {pulsar ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-info">
            <span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-info" />
            ao vivo
          </span>
        ) : null}
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{rotulo}</p>
      <p className="truncate text-xl font-semibold tabular-nums text-ink">{valor}</p>
      <p className="text-xs text-ink-muted">{apoio}</p>
      {acao === null ? null : <div className="mt-auto -mb-1 -ml-2 pt-1">{acao}</div>}
    </li>
  );
}
